const VARIANT_UPDATE_EVENT = 'variant:update';
const SIZE_MATCH_VIEW = 'thermite-size-match';
const SIZE_MATCH_SECTION_KEY = 'thermite:size-match-section-id';

// Most specific first. One pattern used to miss 174 products: commercial (11R22.5,
// 8R19.5) and old metric (185R14) carry no aspect ratio, and flotation section widths
// can be a single digit (29X9R14).
const SIZE_PATTERNS = [
  /\d{2,3}(?:\.\d+)?\s*[\/xX]\s*\d{1,3}(?:\.\d+)?\s*[A-Za-z]{0,3}\s*\d{1,2}(?:\.\d+)?[A-Za-z]{0,2}/,
  /\d{1,3}(?:\.\d{1,2})?\s*[A-Za-z]{1,2}\s*\d{2}(?:\.\d)?/,
];

function extractSize(value) {
  if (!value) return null;
  for (const pattern of SIZE_PATTERNS) {
    const match = String(value).match(pattern);
    if (match) return match[0].replace(/\s+/g, '');
  }
  return null;
}

// The wheel a size fits: 245/45R20 and 33X12.50R20 are both 20.
function rimOf(size) {
  const match = String(size || '').match(/[Rr](\d{2}(?:\.\d)?)[A-Za-z]*$/);
  return match ? match[1] : null;
}

function tagHandle(tag) {
  return tag.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

class ProductRecommendations extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;
      if (mutation.attributeName === 'data-current-size') continue;
      if (mutation.attributeName === 'data-match-level') continue;
      this.#loadRecommendations();
      break;
    }
  });

  #cachedRecommendations = {};
  #activeFetch = null;
  #productData = null;

  #onVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;
    this.#loadRecommendations(String(variantId));
  };

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    if (this.#sizeMatchingEnabled) {
      document.addEventListener(VARIANT_UPDATE_EVENT, this.#onVariantUpdate);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(VARIANT_UPDATE_EVENT, this.#onVariantUpdate);
  }

  get #sizeMatchingEnabled() {
    return this.dataset.matchSize !== 'false' && Boolean(this.dataset.productHandle);
  }

  // Never recommend a tire that does not fit the wheel on screen: exact size, then the
  // same rim diameter, then hide. Falling through to Shopify's generic related products
  // is what put 20" tires on a 14" page.
  async #loadRecommendations(variantId) {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;
    if (!productId || !id) throw new Error('Product ID and an ID attribute are required');
    if (recommendationsPerformed === 'true' && !variantId) return;

    try {
      let size = null;
      if (this.#sizeMatchingEnabled) {
        try {
          size = await this.#currentSize(variantId);
        } catch {
          size = null;
        }
      }

      if (size) {
        const rim = rimOf(size);
        const attempts = [{ tag: tagHandle(size), level: 'size', match: (s) => s === size }];
        if (rim) {
          attempts.push({ tag: `rim-${rim}`, level: 'rim', match: (s) => rimOf(s) === rim });
        }

        for (const attempt of attempts) {
          const section = await this.#matchedSection(attempt.tag, attempt.match);
          if (section) {
            this.#render(section, size, attempt.level);
            return;
          }
        }

        this.#hide();
        return;
      }

      const fallback = await this.#fetchCached(
        `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`
      );
      if (!fallback.success) {
        if (!Shopify.designMode) this.#handleError(new Error(`Server returned ${fallback.status}`));
        return;
      }
      this.#render(this.#extractSection(fallback.data, id), '', 'generic');
    } catch (error) {
      this.#handleError(error);
    }
  }

  async #matchedSection(tag, match) {
    const result = await this.#fetchMatched(tag);
    if (!result?.success || !result.data.includes('resource-list__item')) return null;

    const section = this.#extractSection(result.data, null);
    this.#pruneSlides(section, match);
    return section?.querySelector('.resource-list__item') ? section : null;
  }

  #render(section, size, level) {
    if (!section?.innerHTML || !section.innerHTML.trim().length) {
      this.#hide();
      return;
    }
    this.dataset.recommendationsPerformed = 'true';
    this.dataset.currentSize = size;
    this.dataset.matchLevel = level;
    this.innerHTML = section.innerHTML;
  }

  // Having nothing that fits is normal, so hide quietly rather than log an error.
  #hide() {
    this.dataset.recommendationsPerformed = 'true';
    this.classList.add('hidden');
  }

  #extractSection(data, id) {
    const html = document.createElement('div');
    html.innerHTML = data || '';
    return id
      ? html.querySelector(`product-recommendations[id="${id}"]`)
      : html.querySelector('product-recommendations');
  }

  // The section id belongs to the collection template, so the first call fetches the whole
  // page to learn it. Cached in localStorage so a returning shopper never re-pays that.
  async #fetchMatched(tag) {
    const base = `${Shopify.routes.root}collections/all/${tag}?view=${SIZE_MATCH_VIEW}&sort_by=best-selling`;
    const cachedSectionId = this.#readStore(SIZE_MATCH_SECTION_KEY);

    if (cachedSectionId) {
      const result = await this.#fetchCached(`${base}&section_id=${cachedSectionId}`);
      if (result.success) return result;
      this.#writeStore(SIZE_MATCH_SECTION_KEY, null);
    }

    const result = await this.#fetchCached(base);
    if (!result.success) return result;

    const found = new DOMParser().parseFromString(result.data, 'text/html').querySelector('product-recommendations');
    if (!found) return null;

    const sectionId = found.id.replace(/^product-recommendations-/, '');
    if (sectionId) this.#writeStore(SIZE_MATCH_SECTION_KEY, sectionId);
    return { success: true, data: found.outerHTML };
  }

  #readStore(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  #writeStore(key, value) {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      /* private mode */
    }
  }

  async #currentSize(variantId) {
    const handle = this.dataset.productHandle;
    if (!handle) return null;

    if (!this.#productData) {
      this.#productData = fetch(`${Shopify.routes.root}products/${handle}.js`).then((response) => {
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        return response.json();
      });
    }

    const product = await this.#productData;
    const variants = product?.variants || [];
    const id = variantId || new URLSearchParams(window.location.search).get('variant');
    const variant =
      (id && variants.find((item) => String(item.id) === String(id))) ||
      variants.find((item) => item.available) ||
      variants[0];
    if (!variant) return null;

    const sizeIndex = (product.options || []).findIndex(
      (option) => /size/i.test(typeof option === 'string' ? option : option.name)
    );
    const candidates = sizeIndex >= 0 ? [variant.options[sizeIndex]] : variant.options || [];
    for (const candidate of candidates) {
      const size = extractSize(candidate);
      if (size) return size;
    }
    return extractSize(variant.title);
  }

  // The section stamps each card with "<size>:<variant id>|..." for every size it can sell
  // right now. Liquid cannot know which size the shopper is on, but we can.
  #variantMap(slide) {
    const card = slide.querySelector('[data-available-sizes]');
    if (!card) return [];
    return (card.dataset.availableSizes || '')
      .split('|')
      .filter(Boolean)
      .map((entry) => {
        const split = entry.lastIndexOf(':');
        return split < 0
          ? { size: extractSize(entry), id: null }
          : { size: extractSize(entry.slice(0, split)), id: entry.slice(split + 1) };
      });
  }

  #cardFits(slide, match) {
    const entries = this.#variantMap(slide);
    // A render without the stamp predates this check; leave it rather than blank the row.
    if (!entries.length) return true;
    return entries.some((entry) => match(entry.size));
  }

  // Point the card at the size the shopper is actually on. Without this the card keeps the
  // product's own first variant, so a 205/50R15 search opened a 205/60R13.
  #retargetLinks(slide, match) {
    const hit = this.#variantMap(slide).find((entry) => entry.id && match(entry.size));
    if (!hit) return;

    for (const link of slide.querySelectorAll('a[href*="/products/"]')) {
      const href = link.getAttribute('href');
      if (!href) continue;
      const url = new URL(href, window.location.origin);
      url.searchParams.set('variant', hit.id);
      link.setAttribute('href', `${url.pathname}${url.search}`);
    }
  }

  #pruneSlides(recommendations, match) {
    const handle = this.dataset.productHandle;
    if (!recommendations || !handle) return;
    const max = Number(this.dataset.maxProducts) || 4;

    for (const scroller of recommendations.querySelectorAll('slideshow-slides, .resource-list--grid')) {
      for (const slide of Array.from(scroller.children)) {
        if (slide.querySelector(`a[href*="/products/${handle}"]`)) {
          slide.remove();
          continue;
        }
        if (!this.#cardFits(slide, match)) slide.remove();
        else this.#retargetLinks(slide, match);
      }

      const remaining = Array.from(scroller.children);
      remaining.slice(max).forEach((slide) => slide.remove());
      remaining.slice(0, max).forEach((slide, index) => {
        if (slide.tagName.toLowerCase() !== 'slideshow-slide') return;
        slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
        slide.style.setProperty('view-timeline-name', `--slide-${index}`);
      });
    }
  }

  async #fetchCached(url) {
    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) return { success: true, data: cachedResponse };

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) return { success: false, status: response.status };
      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
